import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { Download, Filter, X, AlertTriangle, Pencil, Plus, Mail, Phone, ExternalLink, User } from "lucide-react";

const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-[#0A192F]";
const yn = (v) => (v === true ? "Yes" : v === false ? "No" : "—");

export default function AdminInterests() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [status, setStatus] = useState("");
  const [duplicate, setDuplicate] = useState(false);
  const [q, setQ] = useState("");
  const [data, setData] = useState({ items: [], status_counts: {}, pipeline: [], total: 0, duplicate_count: 0 });
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [editEvent, setEditEvent] = useState(null); // event object or {} for new
  const [notifyEmail, setNotifyEmail] = useState("");
  const [savingNotify, setSavingNotify] = useState(false);

  const loadEvents = useCallback(() => {
    api.get("/admin/interest-events").then(({ data }) => setEvents(data.items || []));
    api.get("/admin/interest-config").then(({ data }) => setNotifyEmail(data.notification_email || "")).catch(() => {});
  }, []);

  const saveNotify = async () => {
    setSavingNotify(true);
    try {
      const { data } = await api.put("/admin/interest-config", { notification_email: notifyEmail });
      setNotifyEmail(data.notification_email);
      toast.success("Free-profile notification email saved");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSavingNotify(false); }
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (eventId) params.event_id = eventId;
    if (status) params.status = status;
    if (duplicate) params.duplicate = true;
    if (q.trim()) params.q = q.trim();
    api.get("/admin/interests", { params })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [eventId, status, duplicate, q]);

  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load]);

  const updateStatus = async (it, newStatus) => {
    try {
      await api.patch(`/admin/interests/${it.id}`, { status: newStatus });
      setData((d) => ({ ...d, items: d.items.map((x) => x.id === it.id ? { ...x, status: newStatus } : x) }));
      if (detail?.id === it.id) setDetail((x) => ({ ...x, status: newStatus }));
      toast.success(`Status → ${newStatus}`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const resolveDup = async (it, resolution) => {
    try {
      await api.patch(`/admin/interests/${it.id}`, { duplicate_review_status: resolution });
      toast.success(resolution === "resolved" ? "Marked as reviewed" : "Updated");
      load();
      if (detail?.id === it.id) setDetail((x) => ({ ...x, duplicate_review_status: resolution }));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const exportCsv = () => {
    const params = {};
    if (eventId) params.event_id = eventId;
    if (status) params.status = status;
    downloadFile("/admin/interests/export.csv", params, "player_interests.csv")
      .catch(() => toast.error("Export failed"));
  };

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 data-testid="admin-interests-title" className="font-display text-3xl uppercase tracking-tighter text-[#0A192F]">Player Interests</h1>
          <p className="text-sm text-slate-500 mt-1">Event interest submissions & recruiting pipeline</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="new-event-btn" onClick={() => setEditEvent({})} className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-sm text-sm font-semibold uppercase tracking-wider hover:border-[#0A192F]">
            <Plus className="w-4 h-4" /> New Event
          </button>
          <button data-testid="export-interests-btn" onClick={exportCsv} className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A192F] text-white rounded-sm text-sm font-semibold uppercase tracking-wider hover:bg-[#1a2a4a]">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Events row */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 mb-4 flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Free-profile alerts →</span>
        <input data-testid="free-profile-notify-email" type="email" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} placeholder="flipapair@gmail.com" className="px-3 py-1.5 border border-slate-300 rounded-sm text-sm w-64" />
        <button data-testid="save-notify-email" onClick={saveNotify} disabled={savingNotify} className="px-3 py-1.5 bg-[#0A192F] text-white text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60">{savingNotify ? "Saving…" : "Save"}</button>
        <span className="text-xs text-slate-400">Per-event alert emails are set in each event's editor.</span>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <EventChip active={eventId === ""} onClick={() => setEventId("")} label="All Events"
          sub={`${events.reduce((a, e) => a + (e.interest_count || 0), 0)} interested`} testid="event-chip-all" />
        {events.map((ev) => (
          <div key={ev.id} className="flex items-center">
            <EventChip active={eventId === ev.id} onClick={() => setEventId(ev.id)} label={ev.name}
              sub={`${ev.interest_count ?? 0} interested${(ev.age_divisions || []).length ? " · " + (ev.age_divisions).join("/") : ""}`}
              testid={`event-chip-${ev.slug}`} />
            <button data-testid={`edit-event-${ev.slug}`} onClick={() => setEditEvent(ev)} title="Edit event" className="ml-1 p-1.5 text-slate-400 hover:text-[#0A192F]"><Pencil className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button data-testid="status-filter-all" onClick={() => setStatus("")} className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm border ${status === "" ? "bg-[#0A192F] text-white border-[#0A192F]" : "border-slate-300 text-slate-600 hover:border-[#0A192F]"}`}>All ({data.total})</button>
        {(data.pipeline || []).map((s) => (
          <button key={s} data-testid={`status-filter-${s.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setStatus(s)}
            className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm border ${status === s ? "bg-[#0A192F] text-white border-[#0A192F]" : "border-slate-300 text-slate-600 hover:border-[#0A192F]"}`}>
            {s} {data.status_counts[s] ? `(${data.status_counts[s]})` : ""}
          </button>
        ))}
        <button data-testid="duplicate-filter" onClick={() => setDuplicate((v) => !v)}
          className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm border ${duplicate ? "bg-amber-500 text-white border-amber-500" : "border-amber-400 text-amber-600 hover:bg-amber-50"}`}>
          <AlertTriangle className="w-3.5 h-3.5" /> Possible Duplicates {data.duplicate_count ? `(${data.duplicate_count})` : ""}
        </button>
        <div className="relative ml-auto">
          <Filter className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="interest-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="pl-9 pr-3 py-2 border border-slate-300 rounded-sm text-sm w-64" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-left">
            <tr className="text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Player</th>
              <th className="px-4 py-3">Class / Pos</th>
              <th className="px-4 py-3">Event</th>
              <th className="px-4 py-3">WWBA</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="interests-table-body">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400 text-xs uppercase tracking-wider">Loading…</td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400 text-sm" data-testid="interests-empty">No interest submissions yet.</td></tr>
            ) : data.items.map((it) => {
              const p = it.player, wb = it.wwba || {};
              return (
                <tr key={it.id} data-testid={`interest-row-${it.id}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {it.duplicate_review_status === "pending" && <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" title="Possible duplicate" />}
                      <div>
                        <div className="font-semibold">{p.first_name} {p.last_name}</div>
                        <div className="text-xs text-slate-500">{p.email || "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{[p.graduation_year && `'${String(p.graduation_year).slice(-2)}`, p.primary_position, p.province].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{it.event_name}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{[wb.passport_status, wb.preferred_role].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="px-4 py-3">
                    <select data-testid={`status-select-${it.id}`} value={it.status} onChange={(e) => updateStatus(it, e.target.value)} className="text-xs border border-slate-300 rounded-sm px-2 py-1.5 bg-white">
                      {(data.pipeline || []).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`view-interest-${it.id}`} onClick={() => setDetail(it)} className="text-xs font-semibold text-[#E63946] uppercase tracking-wider">View</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detail && <DetailDrawer it={detail} pipeline={data.pipeline} onClose={() => setDetail(null)} onStatus={updateStatus} onResolveDup={resolveDup} onNotes={load} />}
      {editEvent !== null && <EventEditor event={editEvent} onClose={() => setEditEvent(null)} onSaved={() => { setEditEvent(null); loadEvents(); load(); }} />}
    </AdminLayout>
  );
}

function EventChip({ active, onClick, label, sub, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`text-left px-3 py-1.5 rounded-sm border ${active ? "bg-[#E63946] text-white border-[#E63946]" : "border-slate-300 text-slate-600 hover:border-[#0A192F]"}`}>
      <span className="block text-xs font-semibold uppercase tracking-wider">{label}</span>
      {sub && <span className={`block text-[10px] font-medium ${active ? "text-white/80" : "text-slate-400"}`}>{sub}</span>}
    </button>
  );
}

function DetailDrawer({ it, pipeline, onClose, onStatus, onResolveDup, onNotes }) {
  const p = it.player, wb = it.wwba || {};
  const [notes, setNotes] = useState(it.admin_notes || "");
  const saveNotes = async () => {
    try { await api.patch(`/admin/interests/${it.id}`, { admin_notes: notes }); toast.success("Notes saved"); onNotes(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const Row = ({ label, value }) => (
    <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100 text-sm">
      <span className="text-slate-500">{label}</span><span className="text-slate-800 font-medium text-right">{value || "—"}</span>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="interest-detail-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white h-full overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-[#0A192F] text-white px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-display text-2xl uppercase tracking-tight">{p.first_name} {p.last_name}</div>
            <div className="text-xs text-white/60">{it.event_name}</div>
          </div>
          <button data-testid="detail-close" onClick={onClose} className="p-2 hover:bg-white/10 rounded-sm"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-6">
          {it.duplicate_review_status === "pending" && (
            <div className="border border-amber-300 bg-amber-50 rounded-sm p-4" data-testid="detail-dup-panel">
              <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm"><AlertTriangle className="w-4 h-4" /> Possible Duplicate — needs review</div>
              <p className="text-xs text-slate-600 mt-1">This player was created despite a name + date-of-birth match. Review and resolve.</p>
              <button data-testid="resolve-dup-btn" onClick={() => onResolveDup(it, "resolved")} className="mt-3 px-3 py-2 bg-amber-500 text-white text-xs font-semibold uppercase tracking-wider rounded-sm">Mark Reviewed</button>
            </div>
          )}

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-[#E63946] mb-2">Status</div>
            <select data-testid="detail-status-select" value={it.status} onChange={(e) => onStatus(it, e.target.value)} className="w-full border border-slate-300 rounded-sm px-3 py-2 bg-white text-sm">
              {(pipeline || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-[#E63946] mb-2">Player</div>
            {p.slug && <a href={`/prospects/${p.slug}`} target="_blank" rel="noreferrer" data-testid="detail-profile-link" className="inline-flex items-center gap-1 text-sm text-[#0A192F] font-semibold mb-2"><ExternalLink className="w-3.5 h-3.5" /> View public profile</a>}
            <Row label="Class" value={p.graduation_year} />
            <Row label="Position" value={[p.primary_position, p.secondary_position].filter(Boolean).join(" / ")} />
            <Row label="Bats / Throws" value={[p.bats, p.throws].filter(Boolean).join(" / ")} />
            <Row label="Height / Weight" value={[p.height_cm && `${p.height_cm} cm`, p.weight_kg && `${p.weight_kg} kg`].filter(Boolean).join(" · ")} />
            <Row label="Team" value={p.current_team} />
            <Row label="School" value={p.school} />
            <Row label="Province" value={p.province} />
            <Row label="Account" value={p.user_id ? "Claimed" : "Unclaimed (invite sent)"} />
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-[#E63946] mb-2">Contact</div>
            <Row label={<span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> Player email</span>} value={p.email} />
            <Row label={<span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> Player cell</span>} value={p.phone} />
            <Row label={<span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> Parent</span>} value={p.parent_name} />
            <Row label="Parent email" value={p.parent_email} />
            <Row label="Parent cell" value={p.parent_phone} />
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-[#E63946] mb-2">WWBA Interest</div>
            <Row label="Passport" value={wb.passport_status} />
            <Row label="Division" value={wb.division} />
            <Row label="Preferred role" value={wb.preferred_role} />
            <Row label="Prev CBR Nation" value={yn(wb.previous_cbr_nation)} />
            <Row label="Prev WWBA" value={yn(wb.previous_wwba)} />
            <Row label="Prev Perfect Game" value={yn(wb.previous_pg)} />
            <Row label="Available full dates" value={yn(wb.available_full_tournament)} />
            <Row label="Can travel to Florida" value={yn(wb.can_travel_florida)} />
            <Row label="Parent travelling" value={yn(wb.parent_travelling)} />
            {wb.why_represent && <div className="mt-2 text-sm"><div className="text-slate-500 text-xs uppercase tracking-wider">Why represent CBR</div><p className="text-slate-800 mt-1">{wb.why_represent}</p></div>}
          </div>

          {(wb.emergency_contact_name || wb.emergency_contact_phone || wb.notes) && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-[#E63946] mb-2">Private — Emergency & Travel</div>
              <Row label="Emergency contact" value={wb.emergency_contact_name} />
              <Row label="Emergency phone" value={wb.emergency_contact_phone} />
              {wb.notes && <div className="mt-2 text-sm"><div className="text-slate-500 text-xs uppercase tracking-wider">Notes</div><p className="text-slate-800 mt-1">{wb.notes}</p></div>}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            {it.media?.swing_video_url && <a href={it.media.swing_video_url} target="_blank" rel="noreferrer" className="text-xs text-[#0A192F] font-semibold inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Swing video</a>}
            {it.media?.pitching_video_url && <a href={it.media.pitching_video_url} target="_blank" rel="noreferrer" className="text-xs text-[#0A192F] font-semibold inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Pitching video</a>}
            {it.media?.highlight_video_url && <a href={it.media.highlight_video_url} target="_blank" rel="noreferrer" className="text-xs text-[#0A192F] font-semibold inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Highlight video</a>}
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-[#E63946] mb-2">Admin Notes</div>
            <textarea data-testid="detail-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm" />
            <button data-testid="detail-save-notes" onClick={saveNotes} className="mt-2 px-4 py-2 bg-[#0A192F] text-white text-xs font-semibold uppercase tracking-wider rounded-sm">Save Notes</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EventEditor({ event, onClose, onSaved }) {
  const isNew = !event.id;
  const [f, setF] = useState({
    name: event.name || "", slug: event.slug || "", event_type: event.event_type || "WWBA",
    description: event.description || "", location: event.location || "", hero_image: event.hero_image || "", logo_image: event.logo_image || "",
    start_date: event.start_date || "", end_date: event.end_date || "",
    eligible_grad_years: (event.eligible_grad_years || []).join(", "), age_divisions: (event.age_divisions || []).join(", "),
    eligibility: event.eligibility || "", interest_open_date: event.interest_open_date || "", interest_close_date: event.interest_close_date || "",
    application_deadline: event.application_deadline || "", final_roster_date: event.final_roster_date || "",
    public_visible: event.public_visible !== false, active: event.active !== false, interest_open: event.interest_open !== false,
    application_status: event.application_status || "open", confirmation_message: event.confirmation_message || "",
    notification_email: event.notification_email || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); set("logo_image", data.url); toast.success("Logo uploaded"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Upload failed"); }
  };

  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    const body = {
      ...f,
      eligible_grad_years: f.eligible_grad_years.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => !isNaN(n)),
      age_divisions: f.age_divisions.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (isNew) await api.post("/admin/interest-events", body);
      else await api.patch(`/admin/interest-events/${event.id}`, body);
      toast.success(isNew ? "Event created" : "Event saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const L = ({ label, children }) => (<div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label><div className="mt-1.5">{children}</div></div>);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="event-editor">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white h-full overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-[#0A192F] text-white px-6 py-4 flex items-center justify-between">
          <div className="font-display text-2xl uppercase tracking-tight">{isNew ? "New Event" : "Edit Event"}</div>
          <button data-testid="event-editor-close" onClick={onClose} className="p-2 hover:bg-white/10 rounded-sm"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <L label="Name"><input data-testid="ev-name" className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} /></L>
          <div className="grid grid-cols-2 gap-4">
            <L label="Slug"><input data-testid="ev-slug" className={inputCls} value={f.slug} onChange={(e) => set("slug", e.target.value)} /></L>
            <L label="Event Type"><input data-testid="ev-type" className={inputCls} value={f.event_type} onChange={(e) => set("event_type", e.target.value)} /></L>
          </div>
          <L label="Description"><textarea data-testid="ev-description" rows={3} className={inputCls} value={f.description} onChange={(e) => set("description", e.target.value)} /></L>
          <L label="Location"><input data-testid="ev-location" className={inputCls} value={f.location} onChange={(e) => set("location", e.target.value)} /></L>
          <L label="Hero Image URL"><input data-testid="ev-hero" className={inputCls} value={f.hero_image} onChange={(e) => set("hero_image", e.target.value)} /></L>
          <L label="Event Logo (shown whole on the /trips card · transparent PNG works best)">
            <div className="flex items-center gap-3">
              {f.logo_image && <img src={f.logo_image} alt="" data-testid="ev-logo-preview" className="h-14 w-16 object-contain bg-[#0A192F] rounded-sm p-1" />}
              <label className="cursor-pointer text-xs font-semibold uppercase tracking-wider border border-slate-300 px-3 py-2 rounded-sm hover:bg-slate-50">
                Upload Logo
                <input data-testid="ev-logo-upload" type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
              </label>
              {f.logo_image && <button type="button" data-testid="ev-logo-remove" onClick={() => set("logo_image", "")} className="text-xs text-slate-500 underline">Remove</button>}
            </div>
          </L>
          <div className="grid grid-cols-2 gap-4">
            <L label="Start Date"><input data-testid="ev-start" type="date" className={inputCls} value={f.start_date} onChange={(e) => set("start_date", e.target.value)} /></L>
            <L label="End Date"><input data-testid="ev-end" type="date" className={inputCls} value={f.end_date} onChange={(e) => set("end_date", e.target.value)} /></L>
          </div>
          <L label="Eligible Grad Years (comma separated)"><input data-testid="ev-grad-years" className={inputCls} placeholder="2026, 2027, 2028" value={f.eligible_grad_years} onChange={(e) => set("eligible_grad_years", e.target.value)} /></L>
          <L label="Age Divisions (comma separated)"><input data-testid="ev-divisions" className={inputCls} placeholder="16U, 17U, 18U" value={f.age_divisions} onChange={(e) => set("age_divisions", e.target.value)} /></L>
          <L label="Eligibility"><textarea data-testid="ev-eligibility" rows={2} className={inputCls} value={f.eligibility} onChange={(e) => set("eligibility", e.target.value)} /></L>
          <div className="grid grid-cols-2 gap-4">
            <L label="Application Deadline"><input data-testid="ev-deadline" type="date" className={inputCls} value={f.application_deadline} onChange={(e) => set("application_deadline", e.target.value)} /></L>
            <L label="Final Roster Date"><input data-testid="ev-roster-date" type="date" className={inputCls} value={f.final_roster_date} onChange={(e) => set("final_roster_date", e.target.value)} /></L>
          </div>
          <L label="Application Status">
            <select data-testid="ev-app-status" className={`${inputCls} bg-white`} value={f.application_status} onChange={(e) => set("application_status", e.target.value)}>
              <option value="open">Open</option><option value="closed">Closed</option>
            </select>
          </L>
          <L label="Confirmation Message"><textarea data-testid="ev-confirmation" rows={3} className={inputCls} value={f.confirmation_message} onChange={(e) => set("confirmation_message", e.target.value)} /></L>
          <L label="Notification Email (new submissions alert)"><input data-testid="ev-notification-email" type="email" className={inputCls} placeholder="flipapair@gmail.com" value={f.notification_email} onChange={(e) => set("notification_email", e.target.value)} /></L>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm"><input data-testid="ev-public" type="checkbox" checked={f.public_visible} onChange={(e) => set("public_visible", e.target.checked)} /> Public visible</label>
            <label className="flex items-center gap-2 text-sm"><input data-testid="ev-active" type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} /> Active</label>
            <label className="flex items-center gap-2 text-sm"><input data-testid="ev-open" type="checkbox" checked={f.interest_open} onChange={(e) => set("interest_open", e.target.checked)} /> Interest open</label>
          </div>
          <button data-testid="ev-save" onClick={save} disabled={busy} className="w-full bg-[#E63946] text-white py-3 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60">{busy ? "Saving…" : isNew ? "Create Event" : "Save Changes"}</button>
        </div>
      </div>
    </div>
  );
}
